"use client";

import { buttonVariants } from "@watt/ui/components/ui/button";
import { cn } from "@watt/ui/utils/classname-tailwind-merge";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { ComponentProps, FC } from "react";
import { DayPicker } from "react-day-picker";

export type CalendarProps = ComponentProps<typeof DayPicker>;

/**
 * The caption dropdowns render as native `<select>` elements. Without explicit
 * colours they inherit `text-foreground` (near-white in dark mode) while the
 * browser keeps a light background, making the month/year values invisible.
 * `color-scheme` also tells the browser to render the option list dark.
 */
const captionDropdownClassName =
  "h-8 rounded-md border border-input bg-background px-2 text-foreground text-sm [color-scheme:light] dark:[color-scheme:dark]";

const Calendar: FC<CalendarProps> = ({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) => (
  <DayPicker
    className={cn("p-3", className)}
    classNames={{
      caption: "flex justify-center pt-1 relative items-center",
      caption_dropdowns: "rdp-caption_dropdowns flex gap-1 mh-2",
      caption_label: "text-sm font-medium",
      cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
      day: cn(
        buttonVariants({ variant: "ghost" }),
        "size-9 p-0 font-normal aria-selected:opacity-100"
      ),
      day_disabled: "text-muted-foreground opacity-50",
      day_hidden: "invisible",
      day_outside: "text-muted-foreground opacity-50",
      day_range_end: "day-range-end",
      day_range_middle:
        "aria-selected:bg-accent aria-selected:text-accent-foreground",
      day_selected:
        "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
      day_today: "bg-accent text-accent-foreground",
      dropdown: captionDropdownClassName,
      dropdown_month: captionDropdownClassName,
      dropdown_year: captionDropdownClassName,
      head_cell:
        "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
      head_row: "flex",
      month: "space-y-4",
      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
      nav: "space-x-1 flex items-center",
      nav_button: cn(
        buttonVariants({ variant: "outline" }),
        "size-7 bg-transparent p-0 opacity-50 hover:opacity-100"
      ),
      nav_button_next: "absolute right-1",
      nav_button_previous: "absolute left-1",
      row: "flex w-full mt-2",
      table: "w-full border-collapse space-y-1",
      ...classNames
    }}
    components={{
      IconLeft: () => <ChevronLeftIcon className="!block size-4" />,
      IconRight: () => <ChevronRightIcon className="!block size-4" />
    }}
    fixedWeeks={true}
    showOutsideDays={showOutsideDays}
    {...props}
  />
);
Calendar.displayName = "Calendar";

export { Calendar };
